//
//  SwimList.js
//  
//
//  Created by Daniel Kanaan on 6/15/25.
//

/// src/SwimListPage.js
import React from 'react';
import tzLookup from 'tz-lookup';
import SwimRouteQuickView from './SwimRouteQuickView';

// --- NEW HELPER FUNCTIONS ---
// Formats meters into a human-readable string (km or m)
const formatDistance = (meters) => {
  if (meters == null) return 'N/A';
  if (meters >= 1000) {
    return `${(meters / 1000).toFixed(1)} km`;
  }
  return `${Math.round(meters)} m`;
};

// Formats seconds into HH:MM:SS format
const formatDuration = (totalSeconds) => {
  if (totalSeconds == null || totalSeconds < 0) return '00:00:00';
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  return [hours, minutes, seconds]
    .map(v => String(v).padStart(2, '0'))
    .join(':');
};

const formatDate = (isoString, timeZone = undefined) => {
  if (!isoString) return 'N/A';
  try {
    return new Date(isoString).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: timeZone, // Use the provided timeZone
    });
  } catch (e) {
    // Fallback if timezone is invalid or causes an error (e.g., RangeError)
    console.warn(`Error formatting date with timezone ${timeZone}, falling back:`, e.message);
    return new Date(isoString).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  }
};

const SwimListPage = ({ swims, loading, error, selectedSwimId, onSwimSelect, activeRoute, routeLoading, onCurateArea, onRefresh }) => {
  // Handle loading state
  if (loading && swims.length === 0) {
    return <div className="loading-spinner">Loading recent swims...</div>;
  }

  // Handle error state
  if (error && swims.length === 0) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="swim-list">
      <div className="swim-list-header">
        <h2>{swims.length} {swims.length === 1 ? 'Swim' : 'Swims'} in the Last 7 Days</h2>
        <button type="button" onClick={onRefresh} disabled={loading}>{loading ? 'Refreshing...' : 'Refresh'}</button>
      </div>
      {error && <div className="error-message">{error}</div>}
      {swims.length > 0 && (
        <ul>
          {swims.map((swim) => {
            let swimTimezone;
            if (swim.geoBoundingBox) {
              try {
                const centerLat = swim.geoBoundingBox.minLat + (swim.geoBoundingBox.maxLat - swim.geoBoundingBox.minLat) / 2;
                const centerLng = swim.geoBoundingBox.minLng + (swim.geoBoundingBox.maxLng - swim.geoBoundingBox.minLng) / 2;
                swimTimezone = tzLookup(centerLat, centerLng);
              } catch (e) {
                console.warn(`Client-side timezone lookup failed for swim ${swim.id}:`, e.message);
              }
            }

            return (
              <li
                key={swim.id}
                className={`swim-item ${swim.id === selectedSwimId ? 'selected' : ''}`}
                onClick={() => onSwimSelect(swim.id)}
              >
                <div className="swim-item-main">
                  <div className="swim-item-user-info">
                    <img src={swim.userProfile?.profilePictureUrl || '/placeholder-user.svg'} alt={swim.userProfile?.displayName} />
                    <div className="swim-user-info-stack">
                      <span className="swim-user-name">
                        {swim.userProfile?.displayName || `#${swim.userID?.substring(0, 6)}`}
                      </span>
                    </div>
                  </div>
                  <div className="swim-item-data-grid">
                    <div className="swim-location-row">
                      <span className="swim-stat-value swim-location">{swim.locationName || 'N/A'}</span>
                    </div>
                    <div className="swim-date-row">
                      <div className="swim-date">{formatDate(swim.startDate, swimTimezone)}</div>
                      <div className="swim-timezone">{swimTimezone ? `(${swimTimezone.replace('_', ' ')})` : ''}</div>
                    </div>
                    <div className="swim-data-bottom-row">
                      <div className="swim-stat">
                        <span className="swim-stat-label">Distance</span>
                        <span className="swim-stat-value swim-distance">{formatDistance(swim.totalDistanceMeters)}</span>
                      </div>
                      <div className="swim-stat">
                        <span className="swim-stat-label">Duration</span>
                        <span className="swim-stat-value">{formatDuration(swim.durationSeconds)}</span>
                      </div>
                      <div className="swim-stat">
                        <span className="swim-stat-label">Status</span>
                        <span className="swim-stat-value">
                          {swim.processingStatus === 'ok_no_candidates' && <span title={swim.processingStatus}>👍</span>}
                          {swim.processingStatus === 'ok_no_detailed_matches' && <span title={swim.processingStatus}>⚪</span>}
                          {swim.processingStatus === 'ok_matches_updated' && <span title={swim.processingStatus}>➕</span>}
                          {['ok_no_candidates','ok_no_detailed_matches','ok_matches_updated'].indexOf(swim.processingStatus) === -1 && <span className="error-icon" title={swim.processingStatus}>❗</span>}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                {swim.matchedUserProfiles?.length > 0 && (
                  <div className="matched-users-section">
                    <div className="matched-users-list">
                      {swim.matchedUserProfiles.map(profile => (
                        <div key={profile.displayName  || `#${profile.userID?.substring(0, 6)}`} className="matched-user">
                          <img src={profile.profilePictureUrl || '/placeholder-user.svg'} alt={profile.displayName  || `#${profile.userID?.substring(0, 6)}`} />
                          <span>{profile.displayName  || `#${profile.userID?.substring(0, 6)}`}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {swim.id === selectedSwimId && (
                  <SwimRouteQuickView
                    swim={swim}
                    activeRoute={activeRoute}
                    loading={routeLoading}
                    onCurateArea={onCurateArea}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default SwimListPage;
